import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./lib/auth";
import LegalConsentGate from "./components/LegalConsentGate";
import { getLocalConsentVersion } from "./lib/legalConsent";
import { needsLegalConsent } from "../../shared/legalConsent";
import Login from "./pages/Login";
import Register from "./pages/Register";
import SetupPassword from "./pages/SetupPassword";
import Dashboard from "./pages/Dashboard";
import Drones from "./pages/Drones";
import Logbook from "./pages/Logbook";
import Settings from "./pages/Settings";
import MapPage from "./pages/MapPage";
import RadarPage from "./pages/RadarPage";
import WeatherPage from "./pages/WeatherPage";
import Planning from "./pages/Planning";
import PlanningWizard from "./pages/PlanningWizard";
import Flights from "./pages/Flights";
import Flight from "./pages/Flight";
import Profile from "./pages/Profile";
import DroneHistory from "./pages/DroneHistory";
import Briefings from "./pages/Briefings";
import BriefingDetail from "./pages/BriefingDetail";
import LogbookDetail from "./pages/LogbookDetail";
import PublicProtocol from "./pages/PublicProtocol";
import DjiAnalyze from "./pages/DjiAnalyze";
import Glossary from "./pages/Glossary";
import Resources from "./pages/Resources";
import { CountryInfoList, CountryDetail } from "./pages/CountryInfo";
import EasaReference from "./pages/EasaReference";
import EasaRules from "./pages/EasaRules";
import TcasSettings from "./pages/TcasSettings";
import LicenseCardPage from "./pages/LicenseCardPage";
import NdFilterFinder from "./pages/NdFilterFinder";
import DroneInfo from "./pages/DroneInfo";
import DroneCinematic from "./pages/DroneCinematic";
import DroneSetup from "./pages/DroneSetup";
import BatteryDetail from "./pages/BatteryDetail";
import ExpiryOverview from "./pages/ExpiryOverview";
import NaturschutzMap from "./pages/NaturschutzMap";
import DjiBulkImport from "./pages/DjiBulkImport";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Imprint from "./pages/Imprint";
import Withdrawal from "./pages/Withdrawal";
import MyData from "./pages/MyData";
import Admin from "./pages/Admin";
import AdminUserDetail from "./pages/AdminUserDetail";
import AdminSubscriptions from "./pages/AdminSubscriptions";
import Packages from "./pages/Packages";
import SetupGuide from "./pages/SetupGuide";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import Subscription from "./pages/Subscription";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import SubscriptionLockNotice from "./components/SubscriptionLockNotice";
import FeatureLockNotice from "./components/FeatureLockNotice";
import WebPlatformLockNotice from "./components/WebPlatformLockNotice";
import CookieNotice from "./components/CookieNotice";
import { Outlet } from "react-router-dom";

/**
 * Rahmen für rechtliche Dokumente (AGB/Datenschutz/Impressum/Widerruf):
 * Eingeloggte Nutzer behalten den App-Frame (Layout), nicht eingeloggte
 * Besucher sehen die Seite standalone, damit der Link aus Cookie-Hinweis,
 * öffentlicher Protokoll-Ansicht und Login/Registrierung ohne Login wirkt.
 */
function PrivacyFrame() {
  const { user } = useAuth();
  return user ? <Layout /> : <Outlet />;
}

// Öffentliche Pfade, die auch bei ausstehender Zustimmung erreichbar bleiben
// müssen (sonst könnte man die zu lesenden Dokumente nie öffnen).
const CONSENT_BYPASS_PREFIXES = [
  "/agb",
  "/datenschutz",
  "/impressum",
  "/widerruf",
  "/p/",
  "/setup-password",
];

export default function App() {
  const { isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Lädt …
      </div>
    );
  }

  // Aktive Zustimmung zu AGB + Datenschutz: blockiert die App für eingeloggte
  // Nutzer, deren akzeptierte Version veraltet/fehlt. Die rechtlichen Volltexte
  // bleiben über die Bypass-Pfade erreichbar.
  const acceptedConsentVersion = user?.legalConsentVersion ?? getLocalConsentVersion();
  const isBypassPath = CONSENT_BYPASS_PREFIXES.some((p) =>
    location.pathname.startsWith(p),
  );
  if (user && needsLegalConsent(acceptedConsentVersion) && !isBypassPath) {
    return <LegalConsentGate />;
  }

  return (
    <>
      <SubscriptionLockNotice />
      <FeatureLockNotice />
      <WebPlatformLockNotice />
      <CookieNotice />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/setup-password" element={<SetupPassword />} />

      {/* Öffentliche, schreibgeschützte Protokoll-Ansicht (ohne Login, ohne Abo). */}
      <Route path="/p/:token" element={<PublicProtocol />} />

      {/* Rechtliche Dokumente öffentlich erreichbar (DSGVO-Transparenz + zwei-
         Klick-Erreichbarkeit): müssen auch vor dem Login, aus dem Cookie-Hinweis
         und dem Consent-Schritt aufrufbar sein. Eingeloggte Nutzer behalten
         dabei den App-Frame (Layout), nicht eingeloggte sehen die Seite
         standalone. */}
      <Route element={<PrivacyFrame />}>
        <Route path="/agb" element={<Terms />} />
        <Route path="/datenschutz" element={<Privacy />} />
        {/* Impressum (§ 5 DDG/TMG) öffentlich erreichbar – wie Datenschutz. */}
        <Route path="/impressum" element={<Imprint />} />
        <Route path="/widerruf" element={<Withdrawal />} />
      </Route>

      {/* Standalone (eingeloggt, ohne App-Frame & ohne Abo-Sperre):
         Paketauswahl & Checkout-Rückkehr müssen ohne aktives Abo erreichbar sein. */}
      <Route
        element={
          <ProtectedRoute>
            <Outlet />
          </ProtectedRoute>
        }
      >
        <Route path="/pakete" element={<Packages />} />
        <Route path="/abo/erfolg" element={<CheckoutSuccess />} />
        <Route path="/einrichtung" element={<SetupGuide />} />
      </Route>

      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/abo" element={<Subscription />} />
        <Route path="/" element={<Dashboard />} />
        <Route path="/karte" element={<MapPage />} />
        <Route path="/radar" element={<RadarPage />} />
        <Route path="/wetter" element={<WeatherPage />} />
        <Route path="/drohnen" element={<Drones />} />
        <Route path="/drohnen/:droneId/historie" element={<DroneHistory />} />
        <Route path="/drohnen/:droneId/info" element={<DroneInfo />} />
        <Route path="/drohnen/:droneId/cinematic" element={<DroneCinematic />} />
        <Route path="/drohnen/:droneId/einrichten" element={<DroneSetup />} />
        <Route path="/akku/:droneId/:batteryId" element={<BatteryDetail />} />
        <Route path="/nd-filter" element={<NdFilterFinder />} />
        <Route path="/flugplanung" element={<Planning />} />
        <Route path="/flugplanung/:id" element={<PlanningWizard />} />
        <Route path="/briefings" element={<Briefings />} />
        <Route path="/briefings/:id" element={<BriefingDetail />} />
        <Route path="/logbuch" element={<Logbook />} />
        <Route path="/logbuch/dji-auswertung" element={<DjiAnalyze />} />
        <Route path="/logbuch/dji-bulk" element={<DjiBulkImport />} />
        <Route path="/logbuch/:id" element={<LogbookDetail />} />
        <Route path="/fluege" element={<Flights />} />
        <Route path="/flug/:id" element={<Flight />} />
        <Route path="/profil" element={<Profile />} />
        <Route path="/glossar" element={<Glossary />} />
        <Route path="/ressourcen" element={<Resources />} />
        <Route path="/laender" element={<CountryInfoList />} />
        <Route path="/laender/:iso" element={<CountryDetail />} />
        <Route path="/easa" element={<EasaReference />} />
        <Route path="/regelwerk" element={<EasaRules />} />
        <Route path="/lizenzkarte" element={<LicenseCardPage />} />
        <Route path="/tcas" element={<TcasSettings />} />
        <Route path="/ablauf" element={<ExpiryOverview />} />
        <Route path="/naturschutz" element={<NaturschutzMap />} />
        <Route path="/meine-daten" element={<MyData />} />
        <Route path="/einstellungen" element={<Settings />} />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <Admin />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/abos"
          element={
            <AdminRoute>
              <AdminSubscriptions />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users/:id"
          element={
            <AdminRoute>
              <AdminUserDetail />
            </AdminRoute>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
